import { useCallback, useRef, useState } from 'react';

/**
 * Controlled/uncontrolled value pattern.
 *
 * - If `value` is provided, it is used (controlled mode).
 * - Otherwise the internal state seeded with `defaultValue` is used.
 * - `onChange` is called in both modes.
 */
export function useControllableState<T>(
  value: T | undefined,
  defaultValue: T,
  onChange?: (next: T) => void,
): [T, (next: T | ((prev: T) => T)) => void] {
  const isControlled = value !== undefined;
  const [internal, setInternal] = useState(defaultValue);
  const current = isControlled ? value : internal;

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      const resolved =
        typeof next === 'function' ? (next as (prev: T) => T)(current) : next;
      if (!isControlled) {
        setInternal(resolved);
      }
      onChangeRef.current?.(resolved);
    },
    [current, isControlled],
  );

  return [current, setValue];
}
